import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";

const PATH = "/slaughterhouse-software";
const URL = `https://samperlabs.com${PATH}`;

const carnitrackApp = {
  "@type": "SoftwareApplication",
  "@id": "https://samperlabs.com/#carnitrack-app",
  name: "CarniTrack",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  url: "https://carnitrack.samperlabs.com/",
  description:
    "Processing intelligence software for slaughterhouse operations — intake through yield with real-time tracking, structured logs, and operational analytics.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  publisher: { "@id": "https://samperlabs.com/#organization" },
};

export default function SlaughterhouseSoftwarePage() {
  const { t } = useTranslation();
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${URL}#webpage`,
        url: URL,
        name: t("seoPages.slaughterhouse.title"),
        description: t("seoPages.slaughterhouse.description"),
        isPartOf: { "@id": "https://samperlabs.com/#website" },
        about: { "@id": "https://samperlabs.com/#carnitrack-app" },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${URL}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: t("seoPages.breadcrumb.home"),
            item: "https://samperlabs.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: t("footer.slaughterhouseSoftware"),
            item: URL,
          },
        ],
      },
      carnitrackApp,
    ],
  };

  return (
    <>
      <SEOHead
        title={t("seoPages.slaughterhouse.title")}
        description={t("seoPages.slaughterhouse.description")}
        canonicalPath={PATH}
        jsonLd={jsonLd}
      />
      <main className="relative z-10 px-4 pb-24 pt-28 sm:px-6">
        <article className="mx-auto max-w-3xl" itemScope itemType="https://schema.org/Article">
          <header className="mb-12">
            <p className="mb-3 text-sm font-medium uppercase tracking-wider text-blue-400">
              CarniTrack
            </p>
            <h1 className="text-3xl font-semibold leading-tight text-white md:text-4xl" itemProp="headline">
              {t("seoPages.slaughterhouse.h1")}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/55" itemProp="description">
              {t("seoPages.slaughterhouse.intro")}
            </p>
          </header>

          <div className="space-y-10 text-white/55">
            <section aria-labelledby="sh-h2-1">
              <h2 id="sh-h2-1" className="mb-4 text-xl font-semibold text-white">
                {t("seoPages.slaughterhouse.h2_1")}
              </h2>
              <p className="leading-relaxed">{t("seoPages.slaughterhouse.p1")}</p>
            </section>
            <section aria-labelledby="sh-h2-2">
              <h2 id="sh-h2-2" className="mb-4 text-xl font-semibold text-white">
                {t("seoPages.slaughterhouse.h2_2")}
              </h2>
              <p className="leading-relaxed">{t("seoPages.slaughterhouse.p2")}</p>
            </section>
            <section aria-labelledby="sh-h2-3">
              <h2 id="sh-h2-3" className="mb-4 text-xl font-semibold text-white">
                {t("seoPages.slaughterhouse.h2_3")}
              </h2>
              <p className="leading-relaxed">{t("seoPages.slaughterhouse.p3")}</p>
            </section>
            <section aria-labelledby="sh-h2-4">
              <h2 id="sh-h2-4" className="mb-4 text-xl font-semibold text-white">
                {t("seoPages.slaughterhouse.h2_4")}
              </h2>
              <p className="leading-relaxed">{t("seoPages.slaughterhouse.p4")}</p>
            </section>
            <nav aria-labelledby="sh-related" className="rounded-xl border border-white/10 bg-white/[0.02] p-6">
              <h2 id="sh-related" className="text-lg font-semibold text-white">
                {t("seoPages.relatedTitle")}
              </h2>
              <ul className="mt-3 list-inside list-disc space-y-2 text-white/70 marker:text-white/40">
                <li>
                  <Link to="/livestock-management" className="text-blue-400 underline-offset-2 hover:underline">
                    {t("footer.livestockManagement")}
                  </Link>
                </li>
                <li>
                  <Link to="/protein-supply-chain" className="text-blue-400 underline-offset-2 hover:underline">
                    {t("footer.proteinSupplyChain")}
                  </Link>
                </li>
              </ul>
            </nav>
            <section aria-labelledby="sh-h2-5" className="rounded-2xl border border-white/10 bg-[#111113]/80 p-8 backdrop-blur">
              <h2 id="sh-h2-5" className="mb-4 text-xl font-semibold text-white">
                {t("seoPages.slaughterhouse.h2_5")}
              </h2>
              <p className="mb-6 leading-relaxed">{t("seoPages.slaughterhouse.p5")}</p>
              <p className="mb-4 text-sm text-white/40">{t("seoPages.slaughterhouse.ctaHint")}</p>
              <Link
                to="/#contact"
                className="btn-primary inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-white"
              >
                {t("seoPages.slaughterhouse.cta")}
              </Link>
            </section>
          </div>
        </article>
      </main>
    </>
  );
}
